import { t } from "i18next";
import { Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ClientColumn } from "./ClientColumn";
import { useTenantClientSelector } from "./useTenantClientSelector";

import Icons from "@/components/icons";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from "@/components/ui/v2/command";
import { PopoverContent } from "@/components/ui/v2/popover";
import { ROUTES } from "@/constants/routes";
import { useTenantsInfinite } from "@/features/auth/api";
import { useClient } from "@/features/clients/api";
import { AddTenantDialog } from "@/features/tenant";
import { TenantProvider } from "@/lib/contexts/Tenant/TenantProvider";
import { useAuth } from "@/lib/hooks/useAuth";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { useInfiniteScroll } from "@/lib/hooks/useInfiniteScroll";
import { useTenants } from "@/lib/hooks/useTenants";
import { getOverviewPageForPath } from "@/lib/utils/clientRouteUtils";

type TenantClientSelectorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  addTenant: ReturnType<typeof useTenants>["addTenant"];
  tenantId: string;
  setTenantId: (id: string) => void;
};

const TenantClientSelector: FC<TenantClientSelectorProps> = ({
  open,
  onOpenChange,
  addTenant,
  setTenantId,
  tenantId,
}) => {
  const [tenantSearch, setTenantSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [addTenantDialogOpen, setAddTenantDialogOpen] = useState(false);
  const [expandedTenantId, setExpandedTenantId] = useState<string | null>(null);
  const [highlightedTenantId, setHighlightedTenantId] = useState<
    string | undefined
  >(undefined);
  const tenantSearchInputRef = useRef<HTMLInputElement>(null);
  const clientSearchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams();
  const { context } = useAuth();
  const { openedFromClientTrigger } = useTenantClientSelector();
  const { data: client } = useClient(clientId);

  const debouncedTenantSearch = useDebounce(tenantSearch, 300);
  const {
    data: tenantsData,
    isLoading: tenantsLoading,
    isFetching: tenantsFetching,
    isFetchingNextPage: tenantsFetchingNextPage,
    hasNextPage: tenantsHasNextPage,
    fetchNextPage: tenantsFetchNextPage,
  } = useTenantsInfinite({ search: debouncedTenantSearch || undefined });

  const tenantsSearching =
    tenantsLoading || (tenantsFetching && !tenantsFetchingNextPage);

  const tenants = useMemo(
    () => tenantsData?.pages.flatMap((p) => p.data) ?? [],
    [tenantsData],
  );

  const { handleScroll: handleTenantScroll } = useInfiniteScroll({
    onFetchMore: tenantsHasNextPage ? tenantsFetchNextPage : undefined,
    hasMore: Boolean(tenantsHasNextPage),
    isLoading: tenantsLoading,
    isFetching: tenantsFetchingNextPage,
  });

  useEffect(() => {
    if (openedFromClientTrigger && tenantId) {
      setExpandedTenantId(tenantId);
    }
  }, [openedFromClientTrigger, tenantId]);

  // Reset search and expanded state when popover closes so list and inputs are in sync on reopen.
  const [prevOpen, setPrevOpen] = useState(open);
  useEffect(() => {
    if (prevOpen && !open) {
      setTenantSearch("");
      setClientSearch("");
      setExpandedTenantId(null);
    }
    setPrevOpen(open);
  }, [open, prevOpen]);

  const showClientPane = expandedTenantId !== null;
  const clientPaneTenantId =
    expandedTenantId ?? (openedFromClientTrigger ? tenantId : null);
  const showRightPane =
    showClientPane && context === "admin" && !!clientPaneTenantId;

  const handleAddTenantSuccess = () => {
    setAddTenantDialogOpen(false);
  };

  const handleTenantSelect = (id: string) => {
    const wasAlreadySelected = id === tenantId;
    // When opened from client trigger, only expand the client list; do not update
    // global tenant (avoids ClientTenantGuard redirect to /clients).
    if (!openedFromClientTrigger) {
      setTenantId(id);
      // If we're on a client page and the current client belongs to another tenant,
      // navigate to Clients Overview so we don't keep a stale client in context.
      if (clientId && client?.tenantId != null && client.tenantId !== id) {
        navigate(`/${ROUTES.clients}`);
      }
    }
    setExpandedTenantId(id);
    setClientSearch(""); // Reset client search when tenant changes
    if (wasAlreadySelected) {
      setTimeout(() => clientSearchInputRef.current?.focus(), 150);
    }
  };

  const handleClientSelect = (clientId2: string, clientName: string) => {
    const pathParts = location.pathname.split("/");
    const currentClientIndex = pathParts.indexOf(clientId ?? "");
    const hasClientInPath = clientId && currentClientIndex !== -1;

    if (hasClientInPath) {
      // Redirect to the corresponding overview page (campaigns, segments, users)
      // instead of keeping the same sub-path (e.g. campaign/123 -> campaigns).
      const overviewPage = getOverviewPageForPath(location.pathname, clientId);
      navigate(`/${ROUTES.clients}/${clientId2}/${overviewPage}`, {
        state: { clientName },
      });
    } else {
      navigate(`/${ROUTES.clients}/${clientId2}/${ROUTES.home}`, {
        state: { clientName },
      });
    }
    onOpenChange(false);
    setTenantSearch("");
    setClientSearch("");
  };

  const handleCreateTenantClick = () => {
    onOpenChange(false);
    setAddTenantDialogOpen(true);
  };

  const handleCreateClientClick = () => {
    onOpenChange(false);
    navigate(`/${ROUTES.clients}/${ROUTES.new}`);
  };

  return (
    <>
      <PopoverContent
        align="start"
        className="w-[600px] p-0"
        sideOffset={8}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          if (openedFromClientTrigger) {
            setTimeout(() => clientSearchInputRef.current?.focus(), 150);
          } else {
            tenantSearchInputRef.current?.focus();
          }
        }}
      >
        <div
          className="flex h-[300px] overflow-hidden"
          onPointerLeave={() => setHighlightedTenantId(undefined)}
        >
          {/* Left Column - Tenants */}
          <div className="flex flex-col flex-1 min-w-0 border-r h-full">
            <Command
              shouldFilter={false}
              value={highlightedTenantId ?? tenantId}
              onValueChange={setHighlightedTenantId}
              className="flex flex-col h-full"
            >
              <CommandInput
                ref={tenantSearchInputRef}
                placeholder="Find Tenant..."
                value={tenantSearch}
                onValueChange={setTenantSearch}
                clearable
              />
              <CommandList
                className="flex-1 overflow-y-auto"
                onScroll={handleTenantScroll}
              >
                {tenantsSearching && (
                  <CommandLoading
                    label={t("common.loading", { defaultValue: "Loading..." })}
                  >
                    <div className="flex justify-center py-4">
                      <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    </div>
                  </CommandLoading>
                )}
                {!tenantsLoading && !tenantsSearching && tenants.length === 0 && (
                  <div
                    data-slot="command-empty"
                    className="py-6 text-center text-sm text-muted-foreground"
                  >
                    {tenantSearch && "No tenants found" }
                  </div>
                )}
                {!tenantsLoading && !tenantsSearching && tenants.length > 0 && (
                  <CommandGroup heading="Tenants">
                    <>
                      {tenants.map(({ name, id }) => {
                        const isSelected = id === tenantId;
                        return (
                          <CommandItem
                            key={id}
                            value={id}
                            onSelect={() => handleTenantSelect(id)}
                            className="flex items-center justify-between gap-2"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <Icons.DomainIcon
                                fontSize={20}
                                className="shrink-0 text-muted-foreground"
                              />
                              <span className="truncate">{name}</span>
                            </div>
                            {isSelected && (
                              <Icons.CheckIcon
                                className="shrink-0 text-foreground"
                                fontSize={16}
                              />
                            )}
                          </CommandItem>
                        );
                      })}
                      {tenantsFetchingNextPage && (
                        <div className="py-2 text-center text-sm text-muted-foreground">
                          {t("common.loadingMore", {
                            defaultValue: "Loading more...",
                          })}
                        </div>
                      )}
                    </>
                  </CommandGroup>
                )}
              </CommandList>
              <div className="border-t p-2 mt-auto">
                <CommandItem
                  onSelect={handleCreateTenantClick}
                  className="cursor-pointer"
                >
                  <Icons.AddIcon
                    fontSize={16}
                    className="text-muted-foreground"
                  />
                  <span>
                    {t("superAdmin.tenant.createTenant", {
                      defaultValue: "Create Tenant",
                    })}
                  </span>
                </CommandItem>
              </div>
            </Command>
          </div>

          {/* Right Column - Clients (animated, shown on tenant click) */}
          <AnimatePresence initial={false}>
            {showRightPane && (
              <motion.div
                key="client-pane"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 300, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{
                  width: { duration: 0.2, ease: "easeOut" },
                  opacity: { duration: 0.2, ease: "easeOut" },
                }}
                className="flex flex-col h-full overflow-hidden border-l shrink-0"
              >
                <ClientColumn
                  tenantId={clientPaneTenantId}
                  clientSearch={clientSearch}
                  setClientSearch={setClientSearch}
                  selectedClientId={clientId}
                  onClientSelect={handleClientSelect}
                  onCreateClient={handleCreateClientClick}
                  clientSearchInputRef={clientSearchInputRef}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </PopoverContent>
      <TenantProvider>
        <AddTenantDialog
          open={addTenantDialogOpen}
          onOpenChange={setAddTenantDialogOpen}
          onSuccess={handleAddTenantSuccess}
          addTenant={addTenant}
        />
      </TenantProvider>
    </>
  );
};

export { TenantClientSelector };
